import {Component, EventEmitter, Input, Output, OnDestroy, ChangeDetectorRef} from '@angular/core';
import {WorkflowVariableDescriptor} from '../../conditions/types';
import {Ast} from '@ngmedax/expression-compiler';
import * as moment from 'moment';
import {TranslationEventService, TranslationService, DATE_FORMAT_YMD_HM, DATE_FORMAT_YMD, DATE_FORMAT_YM, DATE_FORMAT_Y} from '@ngmedax/translation';
import IMask from 'imask/esm/imask';

@Component({
  selector: 'app-workflow-conditions-row',
  templateUrl: './workflow-conditions-row.component.html',
  styleUrls: ['./workflow-conditions-row.component.css']
})
export class WorkflowConditionsRowComponent implements OnDestroy {
  @Input() variables: WorkflowVariableDescriptor[] = [];
  @Input() expr: Ast.Expression;
  @Output() exprChange = new EventEmitter<Ast.Expression>();
  @Output() exprDelete = new EventEmitter<void>();

  public currentType: 'number'|'string'|'date'|'boolean' = 'string';
  public datePlaceholder = 'YYYY|YYYY-MM|YYYY-MM-DD|YYYY-MM-DDTHH:mm';
  public iMaskOptions: any = null;
  public dateFormatMap: any = {};
  public dateFormatRegExpMap: any = {};
  public dateFormat: string = '';
  private dateFormatRegExp: RegExp = /$/;
  private dateChangedSub: any = null;
  private acceptedDate: string = '';
  private isLocaleChangeRebuild = false;
  private browserSupportsDateInput: boolean | null = null;

  public constructor(
    private translationEvents: TranslationEventService,
    private translationService: TranslationService,
    private ref: ChangeDetectorRef,
  ) {}

  public ngOnInit() {
    this.setDateFormatMap();
    this.setDateFormatRegExpMap();

    if (this.translationEvents) {
      this.dateChangedSub = this.translationEvents.onLocaleChanged().subscribe(() => {
        this.isLocaleChangeRebuild = true;
        this.setDateFormatMap();
        this.dateFormat = '';
        this.ref.markForCheck();
        this.ref.detectChanges();
        this.syncFromRule();
        this.ref.markForCheck();
        this.ref.detectChanges();
        this.isLocaleChangeRebuild = false;
      });
    }
    this.syncFromRule();
  }

  public ngOnDestroy() {
    this.dateChangedSub && this.dateChangedSub.unsubscribe && this.dateChangedSub.unsubscribe();
  }

  public onVariableChange() {
    this.expr.value = 0;
    this.syncFromRule();
    this.currentType === 'string' && (this.expr.value = '');
    this.onExprChange();
  }

  public onExprChange() {
    // ensure numeric inputs are numbers for number type
    if (this.currentType === 'number') {
      const num = typeof this.expr.value === 'string' ? Number((this.expr.value as any)) : this.expr.value;
      if (!Number.isNaN(num)) {
        this.expr.value = num as any;
      }
    }
    this.exprChange.emit(this.expr);
  }

  private syncFromRule() {
    const vd = this.variables.find(v => v.name === this.expr.variable);
    this.currentType = (vd?.valueType || 'string') as any;
    if (this.currentType === 'date') {
      const dk = vd?.dateKind;
      this.datePlaceholder = dk === 'fullDateTime' ? 'YYYY-MM-DDTHH:mm' : dk === 'fullDate' ? 'YYYY-MM-DD' : dk === 'monthDate' ? 'YYYY-MM' : 'YYYY';
      this.initDateFormat(dk);
    }
    this.onExprChange();
  }

  private setDateFormatMap() {
    const get = (fmtToken: string) => this.translationService ? this.translationService.getDateFormat(fmtToken) : fmtToken;
    this.dateFormatMap = {
      fullDateTime:get(DATE_FORMAT_YMD_HM),
      fullDate: get(DATE_FORMAT_YMD),
      monthDate:get(DATE_FORMAT_YM),
      yearDate: get(DATE_FORMAT_Y)
    };
  }

  private setDateFormatRegExpMap() {
    this.dateFormatRegExpMap = {
      fullDateTime: /:(\d{2})(?:\.\d+)?(?:Z|[+-].+)?$/,
      fullDate: /T.*$/,
      monthDate: /-\d{2}T.*$/,
      yearDate: /-.+$/
    };
  }

  private initDateFormat(kind?: 'fullDate'|'fullDateTime'|'monthDate'|'yearDate') {
    const k = kind || 'fullDate';
    this.dateFormat = (this.dateFormatMap && this.dateFormatMap[k]) || 'YYYY-MM-DD';
    this.dateFormatRegExp = this.dateFormatRegExpMap[k] || /$/;
    this.iMaskOptions = {
      mask: Date,
      pattern: this.dateFormat,
      lazy: false,
      min: new Date(1900, 0, 1),
      max: new Date(2038, 0, 19),
      format: (date) => moment(date).format(this.dateFormat),
      parse: (str) => {
        const m = moment(str, this.dateFormat);
        this.acceptedDate = m.toISOString(true).replace(this.dateFormatRegExp, '');
        return m;
      },
      blocks: {
        YYYY: { mask: IMask.MaskedRange, placeholderChar: 'Y', from: 1900, to: 2038 },
        MM: { mask: IMask.MaskedRange, placeholderChar: 'M', from: 1, to: 12 },
        DD: { mask: IMask.MaskedRange, placeholderChar: 'D', from: 1, to: 31 },
        HH: { mask: IMask.MaskedRange, placeholderChar: 'H', from: 0, to: 23 },
        mm: { mask: IMask.MaskedRange, placeholderChar: 'm', from: 0, to: 59 }
      }
    };
  }

  public onDateComplete(nativeValue?: string) {
    if (this.isLocaleChangeRebuild) { return; }
    if (typeof nativeValue === 'string') {
      // native date input yields YYYY-MM-DD
      this.expr.value = nativeValue as any;
      this.onExprChange();
      return;
    }
    if (this.acceptedDate) {
      this.expr.value = this.acceptedDate as any;
      this.onExprChange();
    }
  }

  public getDisplayDate(): string {
    return this.expr.value ? moment(this.expr.value as string).format(this.dateFormat): '';
  }

  /**
   * Returns true if browser supports input date format
   * and also has an ui for it
   *
   * @returns {boolean}
   */
  public supportsInputDate(): boolean {
    const input = document.createElement('input');
    input.setAttribute('type','date');

    const notADateValue = 'not-a-date';
    input.setAttribute('value', notADateValue);

    return (input.value !== notADateValue);
  }
}

